import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormControlOptions, FormGroup, ValidatorFn } from '@angular/forms';
import { CollectionFormService } from './collection-form.service';
import { CollectionForm } from '../../../../../models/ts/collection-form.model';
import { CollectionFormField } from '../../../../../models/ts/collection-form-field.model';
import { TableFieldDataType } from '../../../../../models/ts/table-field-data-type.model';
import { FormFieldType } from 'src/models/ts/form-field-type.model';
import { nanValidator } from '../../../../shared/validators/nan-validator';

/**
 * Provides utility functions for FormGroups based off of CollectionForm
 */
@Injectable({
  providedIn: 'root'
})
export class CollectionFormGroupService {

  public constructor(private fb: FormBuilder) {
  }

  public getFormGroupFromData(form: CollectionForm): FormGroup {
    let fields = CollectionFormService.extractFieldsFromForm(form);
    let formulaFields = CollectionFormService.getAllFormFormulasFieldIds(form);

    const formGroup = this.fb.group({});
    fields.forEach((field) => {
      if (field.FormFieldType == FormFieldType.List ||
        (field.FormFieldType == FormFieldType.ChkQuestions && field.ComponentType == TableFieldDataType.ChkQuestionsCreateEditModus)) {
        //formListFieldGroup is not a FormControl but a FormGroup
        formGroup.addControl(field.Id.toString(), this.getFormGroupFromCollectionListField(field, formulaFields));
      } else if (field.ComponentType == TableFieldDataType.OrganizationChartUnitSelector) {
        formGroup.addControl(field.Id.toString(), new FormControl(field.OrgChartUnitSelector, { updateOn: 'blur' }));

      } //Need to make formcontrols update on change for radio and checkbox fields for safari.
      else if (field.ComponentType == TableFieldDataType.RadioGroup ||
        field.ComponentType == TableFieldDataType.Checkbox ||
        field.ComponentType == TableFieldDataType.DatePicker || 
        field.ComponentType == TableFieldDataType.DateTimePicker) {
        formGroup.addControl(field.Id.toString(), new FormControl(field.Value, { updateOn: 'change' }));
      } else if (field.ComponentType == TableFieldDataType.Numeric) {
        formGroup.addControl(field.Id.toString(), new FormControl(field.Value, {
          updateOn: formulaFields.indexOf(field.CollectionFieldsID) == -1 ? 'blur' : 'change',
          validators: nanValidator
        }));
      } else
        formGroup.addControl(field.Id.toString(), new FormControl(field.Value, this.getValidatorOptions(field, formulaFields)));
    });
    return formGroup;
  }

  /**
   * Initializes a FormGroup with a control for each field given.
   * Allows for the property to be specified to use as the key for the form control in case you want to use a different property than the field's Id.
   * Example: Fields in a grid record are defined by their CollectionFieldId, not their Id.
   * @param fields
   * @param property
   * @param formulaFieldIds
   */
  public getFormGroupFromFieldsByProperty(fields: CollectionFormField[], property: keyof CollectionFormField, formulaFieldIds: number[]): FormGroup<{}> {
    const formGroup = this.fb.group({});
    fields.forEach((field) => {
      if (field.FormFieldType == FormFieldType.List ||
        (field.FormFieldType == FormFieldType.ChkQuestions && field.ComponentType == TableFieldDataType.ChkQuestionsCreateEditModus)) {
        //formListFieldGroup is not a FormControl but a FormGroup

        formGroup.addControl(field[property].toString(), this.getFormGroupFromCollectionListField(field, formulaFieldIds));
      } else if (field.ComponentType == TableFieldDataType.OrganizationChartUnitSelector) {
        formGroup.addControl(field[property].toString(), new FormControl(field.OrgChartUnitSelector, { updateOn: 'blur' }));
      } else
        formGroup.addControl(field[property].toString(), new FormControl(field.Value, this.getValidatorOptions(field, formulaFieldIds)));
    });
    return formGroup;
  }

  public getFormGroupFromCollectionListField(field: CollectionFormField, formulaFieldIds: number[]): FormGroup<{}> {
    if (!field.Records) {
      throw new Error('Field is not a list field');
    }
    const formGroup = this.fb.group({});
    field.Records.forEach((record) => {
      //Each record inside formListfield is a formgroup
      const formRecordGroup = this.getFormGroupFromFieldsByProperty(record.Fields, 'CollectionFieldsID', formulaFieldIds);
      formGroup.addControl(record.CrossLinkedInstancesID.toString(), formRecordGroup);
    });
    return formGroup;
  }

  private getValidatorOptions(field: CollectionFormField, formulaFields: number[]): ValidatorFn | ValidatorFn[] | FormControlOptions | null {
    if (field.IsLookupField || field.Evaluations.length > 0 || formulaFields.indexOf(field.CollectionFieldsID) != -1) {
      return null;
    }
    return { updateOn: 'blur' };
  }
}
